/**
 * 診療予約データ管理関連の型定義
 */

/**
 * 通知ステータス
 */
export type NotificationStatus = 'notification_required' | 'prescription_unavailable' | 'none';

/**
 * アクションステータス（後方互換性のため残す）
 */
export type ActionStatus = NotificationStatus;

/**
 * 一括操作タイプ
 */
export type BulkActionType = 'paste' | 'delete' | 'notify';

/**
 * 業務ステータス
 */
export type BusinessStatus = 'completed' | 'uncompleted';

/**
 * 診療予約レコード（DB格納データ）
 */
export interface MedicalReservationRecord {
  id: string;
  upload_batch_id: string;

  // CSV由来の主要データ
  doctorInCharge: string;          // 担当医師（3列目）
  anybotId: string;                // anybot ID（4列目）
  patientName: string;             // 患者名（名前（漢字）14列目）
  consultationStartTime: string;   // 診療開始時間（5列目）

  // システム管理用
  actionStatus: ActionStatus;             // 通知ステータス（旧アクションステータス）
  paste_flag?: boolean;                   // 診療リスト貼り付け完了フラグ
  identity_verification_flag?: boolean;   // 身分証明書確認フラグ
  businessStatus?: BusinessStatus;        // 業務ステータス
  assignedTo?: string;                    // 振り分け先
  notes?: string;                         // 備考・メモ

  // CSV全項目（詳細表示用）
  csvData: {
    [key: string]: string;         // 56列すべてのデータ
  };

  created_at: string;
  updated_at: string;
}

/**
 * 診療予約一覧取得APIレスポンス
 */
export interface MedicalReservationListResponse {
  data: MedicalReservationRecord[];
  meta: {
    current_page: number;
    total_pages: number;
    total_records: number;
    page_size: number;
  };
}

/**
 * 診療予約フィルター・検索オプション
 */
export interface MedicalReservationFilterOptions {
  search?: string;                 // anybot ID or 患者名検索
  doctor?: string;                 // 担当医師フィルター
  actionStatus?: ActionStatus[];   // アクションステータスフィルター
  dateRange?: {
    start: string;                 // 開始日付 (YYYY-MM-DD)
    end: string;                   // 終了日付 (YYYY-MM-DD)
  };
  uploadBatchId?: string;          // アップロードバッチIDフィルター
  sort_by?: keyof MedicalReservationRecord;
  sort_order?: 'asc' | 'desc';
  page?: number;
  page_size?: number;
}

/**
 * 指定時間外診療予約フィルター・検索オプション
 */
export interface OutOfHoursMedicalReservationFilterOptions extends MedicalReservationFilterOptions {
  isOutOfHours?: boolean;  // 時間外フィルタ（診療時間外のデータのみ表示）
  isPast?: boolean;        // 過去の診療開始時間フィルタ
}

/**
 * 診療予約更新APIリクエスト
 */
export interface MedicalReservationUpdateRequest {
  actionStatus?: ActionStatus;
  paste_flag?: boolean;
  identity_verification_flag?: boolean;
  businessStatus?: BusinessStatus;
  assignedTo?: string;
  notes?: string;
  csvData?: {
    [key: string]: string;
  };
}

/**
 * 一括ステータス更新APIリクエスト
 */
export interface BulkStatusUpdateRequest {
  ids: string[];
  actionStatus: ActionStatus;
  assignedTo?: string;
}

/**
 * 診療予約管理状態
 */
export interface MedicalReservationListState {
  data: MedicalReservationRecord[];
  meta: MedicalReservationListResponse['meta'] | null;
  isLoading: boolean;
  error: string | null;
  filters: MedicalReservationFilterOptions;
  selectedRecord: MedicalReservationRecord | null;
  detailModalOpen: boolean;
  editModalOpen: boolean;
  assignModalOpen: boolean;
  selectedIds: string[];
}

/**
 * CSV項目マッピング（56列のCSVヘッダー）
 */
export const CSV_HEADERS = [
  '成果報酬',
  'カウンセラー対応', 
  '担当医師',
  'anybot ID',
  '診療開始時間',
  'tags',
  'ステータス',
  '会員登録',
  '問診回答',
  '再診用問診',
  '電話番号',
  '診療スタイル',
  '使用ツール',
  '名前（漢字）',
  '名前（カナ）',
  '診療リクエスト',
  '遅延',
  '診療対応',
  '中用量処方有無',
  'アフターピル処方有無',
  '副作用改善薬処方有無',
  'CS対応必要有無',
  '医師からCS/カウンセラー',
  'Wチェック',
  'mederi CSから医師へ',
  'カウンセラーから医師へ',
  '年齢',
  '親への同意',
  '服用経験',
  '服用していたピル',
  '改善目的',
  '希望ピル',
  '医師に聞きたいこと',
  '※再診問診 医師にききたいこと',
  '※再診問診 種類変更希望',
  '※再診問診 副作用緩和薬希望',
  '前回生理開始日',
  '月経周期',
  '生理を避けたい日にち',
  '性行為の日時',
  '妊娠希望',
  '妊娠経験',
  '妊娠時期・出産回数',
  '妊娠・出産・授乳有無',
  '過去妊娠中に「黄疸」「ヘルペス」「持続性掻疼症」と診断されたことはありますか？',
  '過去2週間以内に手術',
  '前兆のある片頭痛',
  '飲んでいるお薬',
  'アレルギー',
  '身長',
  '体重',
  'BMIの値は30以下',
  '高血圧診断',
  'タバコ',
  '不正出血',
  '婦人科系疾患',
  'その他疾患',
  'ご家族に血栓症',
  'ご家族に乳がん',
  '身分証',
  '採血画像'
] as const;

/**
 * 貼り付けAPI用の型定義
 */
export interface PasteToSpreadsheetRequest {
  reservation_ids: number[]; // 数値型のID配列
}

export interface PasteToSpreadsheetResponse {
  success: boolean;
  message: string;
  data?: {
    job_id: string;
    total_count: number;
    doctors: Array<{
      doctor_code: string;
      doctor_name: string;
      record_count: number;
      spreadsheet_url: string | null;
    }>;
  };
  errors?: Record<string, string[]>;
}

export interface PasteStatusResponse {
  success: boolean;
  data: {
    job_id: string;
    status: 'processing' | 'completed' | 'failed';
    progress: number;
    total_count: number;
    completed_count: number;
    message?: string;
    error_message?: string;
    results?: Array<{
      doctor_code: string;
      doctor_name: string;
      success: boolean;
      record_count?: number;
      sheet_name?: string;
      spreadsheet_url?: string;
      error?: string;
    }>;
  };
}

/**
 * CSV項目のカテゴリ分け（詳細表示用）
 */
export const CSV_CATEGORIES = {
  basic: {
    label: '基本情報',
    fields: ['成果報酬', 'カウンセラー対応', '担当医師', 'anybot ID', '診療開始時間', 'tags', 'ステータス']
  },
  registration: {
    label: '登録・問診情報', 
    fields: ['会員登録', '問診回答', '再診用問診', '診療リクエスト', '遅延', '診療対応']
  },
  contact: {
    label: '連絡先・診療方法',
    fields: ['電話番号', '診療スタイル', '使用ツール', '名前（漢字）', '名前（カナ）']
  },
  prescription: {
    label: '処方情報',
    fields: ['中用量処方有無', 'アフターピル処方有無', '副作用改善薬処方有無']
  },
  support: {
    label: 'サポート・対応',
    fields: ['CS対応必要有無', '医師からCS/カウンセラー', 'Wチェック', 'mederi CSから医師へ', 'カウンセラーから医師へ']
  },
  patient: {
    label: '患者基本情報',
    fields: ['年齢', '親への同意', '服用経験', '服用していたピル', '改善目的', '希望ピル']
  },
  consultation: {
    label: '相談内容',
    fields: ['医師に聞きたいこと', '※再診問診 医師にききたいこと', '※再診問診 種類変更希望', '※再診問診 副作用緩和薬希望']
  },
  medical: {
    label: '医療・健康情報',
    fields: ['前回生理開始日', '月経周期', '生理を避けたい日にち', '性行為の日時', '妊娠希望', '妊娠経験', '妊娠時期・出産回数', '妊娠・出産・授乳有無']
  },
  health: {
    label: '健康状態',
    fields: ['過去妊娠中に「黄疸」「ヘルペス」「持続性掻疼症」と診断されたことはありますか？', '過去2週間以内に手術', '前兆のある片頭痛', '飲んでいるお薬', 'アレルギー']
  },
  physical: {
    label: '身体情報',
    fields: ['身長', '体重', 'BMIの値は30以下', '高血圧診断', 'タバコ']
  },
  symptoms: {
    label: '症状・疾患',
    fields: ['不正出血', '婦人科系疾患', 'その他疾患', 'ご家族に血栓症', 'ご家族に乳がん']
  },
  documents: {
    label: '書類・画像',
    fields: ['身分証', '採血画像']
  }
} as const;

/**
 * 通知ステータスの表示設定
 */
export const NOTIFICATION_STATUS_CONFIG = {
  prescription_unavailable: {
    label: '処方不可通知',
    color: 'error' as const,
    backgroundColor: '#ffebee',  // 赤色背景
    textColor: '#000000',  // 黒文字
    icon: '⚠️'
  },
  notification_required: {
    label: '要通知',
    color: 'warning' as const,
    backgroundColor: '#fff9c4',  // 黄色背景
    textColor: '#000000',  // 黒文字
    icon: '⚠️'
  },
  none: {
    label: '',
    color: 'default' as const,
    backgroundColor: 'transparent',
    textColor: 'inherit',
    icon: ''
  }
} as const;

/**
 * 業務ステータスの表示設定
 */
export const BUSINESS_STATUS_CONFIG = {
  completed: {
    label: '完了',
    color: 'success' as const,
    backgroundColor: '#e8f5e9',
    textColor: '#000000'
  },
  uncompleted: {
    label: '未完了',
    color: 'default' as const,
    backgroundColor: '#f5f5f5',
    textColor: '#000000'
  }
} as const;

/**
 * 後方互換性のためのエイリアス
 */
export const ACTION_STATUS_CONFIG = NOTIFICATION_STATUS_CONFIG;

/**
 * 指定時間外診療予約表示用レコード（61項目）
 */
export interface OutOfHoursDisplayRecord extends MedicalReservationRecord {
  // 追加の表示用フィールド
  displayData: {
    '成果報酬': string;
    '自動プログラム使用': string;
    '担当カウンセラー': string;
    'カウンセラー対応': string;
    '担当医師': string;
    'anybot ID': string;
    '診療開始時間': string;
    'tags': string;
    'ステータス': string;
    '会員登録': string;
    '問診回答': string;
    '再診用問診': string;
    '登録電話番号': string;
    '診療可能な電話番号': string;
    '使用ツール': string;
    '名前（漢字）': string;
    '名前（カナ）': string;
    '遅延': string;
    '診療対応': string;
    '低用量ピル': string;
    '中用量ピル': string;
    'アフターピル': string;
    '副作用緩和薬': string;
    'バイク便': string;
    'CS対応内容': string;
    '処方チェック': string;
    'mederi CSから医師へ': string;
    '処方可能医薬品': string;
    '年齢': string;
    '親への同意': string;
    '服用経験': string;
    '服用していたピル': string;
    '改善目的': string;
    '希望ピル': string;
    '医師に聞きたいこと': string;
    '再診身分証': string;
    '再診予約理由': string;
    '再診相談内容詳細': string;
    '前回生理開始日': string;
    '次回生理日': string;
    '生理を避けたい日にち': string;
    '性行為の日時': string;
    '妊娠希望': string;
    '妊娠経験': string;
    '妊娠時期・出産回数': string;
    '妊娠・出産・授乳有無': string;
    '過去妊娠中の既往歴': string;
    '手術歴': string;
    '片頭痛': string;
    '既往歴': string;
    'アレルギー': string;
    '身長': string;
    '体重': string;
    'BMI': string;
    '高血圧診断': string;
    'タバコ': string;
    '不正出血': string;
    '婦人科系疾患の既往歴': string;
    'その他疾患の既往歴': string;
    'ご家族に血栓症': string;
    'ご家族に乳がん': string;
    '身分証': string;
    '採血画像': string;
  };
}